import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Layers } from 'lucide-react';
import { DataTable } from '@r/components/data-table/data-table.tsx';
import { DataTableSkeleton } from '@r/components/data-table/data-table-skeleton.tsx';
import { useDataTable } from '@r/hooks/use-data-table.ts';
import { useSegmentsColumns } from './segments-columns.tsx';
import {
  useApiAdminsegmentsGetCollection,
  getApiAdminsegmentsGetCollectionQueryKey
} from '@r/api/generated/client.ts';
import type { SegmentSegmentCollectionReadModel } from '@r/api/generated/client.schemas.ts';
import { useOrvalQueryErrorToast } from '@r/hooks/use-orval-error-toast.ts';
import { useDeleteSegment } from '@admin/hooks/use-delete-segment.ts';

type Segment = SegmentSegmentCollectionReadModel;

interface SegmentListProps {
  onPreview: (segment: Segment) => void;
  onEditDetails: (segment: Segment) => void;
  onManageRules: (segment: Segment) => void;
  refreshKey?: number;
  onDeleted?: () => void;
}

/** Affiche la liste paginée des segments du workspace, avec suppression et actions rapides. */
export function SegmentList({
  onPreview,
  onEditDetails,
  onManageRules,
  refreshKey,
  onDeleted
}: SegmentListProps) {
  const { t } = useTranslation();
  const [apiPagination, setApiPagination] = useState({ pageIndex: 0, pageSize: 30 });

  const params = { page: apiPagination.pageIndex + 1, perPage: apiPagination.pageSize };

  const segmentsQuery = useApiAdminsegmentsGetCollection(params, {
    query: {
      queryKey: [...getApiAdminsegmentsGetCollectionQueryKey(params), refreshKey]
    }
  });
  useOrvalQueryErrorToast(segmentsQuery, t('toast.error.label'));

  const { handleDelete } = useDeleteSegment(onDeleted);

  const data = useMemo(() => segmentsQuery.data?.data ?? [], [segmentsQuery.data]);
  const pagination = segmentsQuery.data?.pagination;

  const mappedTable = useDataTable<Segment, unknown>({
    data,
    columns: useSegmentsColumns(onPreview, onEditDetails, onManageRules, handleDelete),
    pageCount: pagination?.last ?? 1,
    enableAdvancedFilter: false,
    manualPaginate: true,
    manualSort: false,
    manualFilter: false
  });

  const { pageIndex, pageSize } = mappedTable.table.getState().pagination;

  useEffect(() => {
    if (pageIndex !== apiPagination.pageIndex || pageSize !== apiPagination.pageSize) {
      setApiPagination({ pageIndex, pageSize });
    }
  }, [pageIndex, pageSize]);

  const isLoading = segmentsQuery.isLoading || segmentsQuery.isFetching;

  if (isLoading) {
    return <DataTableSkeleton columnCount={5} rowCount={5} />;
  }

  return (
    <DataTable
      table={mappedTable.table}
      totalItems={pagination?.totalItems}
      emptyIcon={Layers}
      emptyMessageKey="admin.segments.empty"
    />
  );
}
